'use client';
import React from 'react';
import useStore, { StoreProps } from '@/app/[locale]/admin/store';
import { ImageInfo } from '../data/dataType';
import { ImageUpload } from './ImageUpload';
import { FaFileImage } from 'react-icons/fa';
import { AttributesInput } from './AttributesInput';
import Modal from './Modal';
import { createPortal } from 'react-dom';
import { imageFields } from '../../app/[locale]/admin/lib/imageFields';
import { setFieldValues } from '../../app/[locale]/admin/lib/util';
import { FormikValues } from 'formik';

export const ImageInput: React.FC<{
    itemId: number | string;
    locale: string;
    bucket: string;
    element: string;
    field: string;
    folder: string;
    sectionId: number;
    value: ImageInfo;
}> = ({ itemId, bucket, element, field, folder, locale, sectionId, value }) => {
    const [imageValue, setImageValue] = React.useState<
        (ImageInfo | undefined) | undefined
    >(value);

    const [attrEditing, setAttrEditing] = React.useState<string | undefined>(
        undefined,
    );
    const setEditedImage = useStore(
        (state: StoreProps) => state.setEditedImage,
    );
    const [_document, set_document] =
        React.useState<globalThis.Document | null>(null);

    React.useEffect(() => {
        set_document(document);
    }, []);

    const changeImageInfo = (imageUploadProps: ImageInfo) => {
        const newValue = {
            ...imageUploadProps,
            id: element,
        };
        setImageValue(newValue);

        if (imageUploadProps) {
            setEditedImage({
                element: element,
                field: field,
                sectionId: sectionId,
                image: newValue,
                dirty: true,
            });
        } else {
            setEditedImage({
                element: element,
                field: field,
                sectionId: sectionId,
                dirty: true,
            });
        }
    };

    const submitChanges = (value: FormikValues, imageInfo: ImageInfo) => {
        if (imageInfo) {
            const attributes = setFieldValues(
                value,
                imageInfo,
                imageFields,
                locale,
            );
            const newValue = { ...imageInfo, ...attributes };
            setImageValue(newValue);
            setEditedImage({
                element: element,
                field: field,
                sectionId: sectionId,
                image: newValue,
                dirty: true,
            });
            setAttrEditing(undefined);
        }
    };

    return (
        <div id={element} key={element}>
            <div className="flex justify-start">
                {imageValue && (
                    <FaFileImage
                        className="absolute z-50 mt-7 ml-2 h-8 w-8 fill-gray-700 stroke-gray-900 border-gray-900 cursor-pointer"
                        onClick={() => setAttrEditing(imageValue?.id)}
                    />
                )}
            </div>
            <div className="flex p-2">
                {_document &&
                    createPortal(
                        <Modal
                            showModal={attrEditing === (imageValue?.id || null)}
                        >
                            <AttributesInput
                                attributes={imageValue}
                                fields={imageFields}
                                locale={locale}
                                onCancel={() => setAttrEditing(undefined)}
                                onSubmitChanges={(value: FormikValues) =>
                                    submitChanges(value, imageValue)
                                }
                                title="Image info"
                            />
                        </Modal>,
                        _document.body,
                    )}
            </div>
            <div className="min-w-fit m-auto">
                <ImageUpload
                    bucket={bucket}
                    folder={folder}
                    id={element}
                    itemId={itemId}
                    locale={locale}
                    multiple={false}
                    onChange={imageUploadProps => {
                        changeImageInfo(imageUploadProps);
                    }}
                    value={imageValue}
                />
            </div>
        </div>
    );
};
